import _ from 'lodash';
import PubNub from 'pubnub/dist/web/pubnub';

/**
 * Wrapper for PubNub JavaScript SDK that uses promises
 */
export class PubnubChannel {
	/**
	 * @param {String} name  The name of the channel
	 * @param {string} userId
	 */
	constructor(name, userId) {
		/**
		 * The channel name
		 * @type {String}
		 */
		this.name = name;
		/**
		 * The chanel name with window.DEPLOYMENT prepended
		 * @type {string}
		 */
		const deploy = window.DEPLOYMENT;
		// prepend, but only once
		this.pubnubName = `${deploy}-${this.name}`.replace(`${deploy}-${deploy}`, deploy);
		/**
		 * The PubNub SDK object
		 * @type {PubNub}
		 */
		this.pubnub = new PubNub({
			subscribeKey: 'sub-c-65b27336-0c95-11e6-a9bb-02ee2ddab7fe',
			publishKey: 'pub-c-88513474-ac85-4a5d-bd8d-40a13c92af9b',
			ssl: true,
			userId: userId || 'anonymous.9be8ba93-c0af-48b2-96cd-b1fe816733fc',
		});
		this.pubnub.subscribe({
			channels: [this.pubnubName],
		});
	}

	/**
	 * Get a stub of this class that can be used in situations like the user is not logged in but we ask for a hub channel
	 * @returns {PubnubChannel}
	 */
	static getStub() {
		return {
			name: 'stub',
			pubnub: undefined,
			subscribe: () => () => {},
			publish: () => Promise.resolve({}),
			hereNow: () => Promise.resolve({}),
			occupancy: () => Promise.resolve({}),
			destroy: () => Promise.resolve({}),
			on: () => () => {},
		};
	}

	/**
	 * Subscribe to events broadcast on the channel
	 * @param {Function} handler  The function to call when an event is received
	 * @param {Options} options  Advanced options to send directly to the PubNub SDK
	 * @returns {Function}  A function to de-register the listener
	 */
	subscribe(handler, options = {}) {
		_.defaults(options, {
			message: data => {
				try {
					handler(data.message, data);
				} catch (e) {
					console.error(`{e.name} {e.message}`, e.stack);
				}
			},
		});
		const deregister = () => {
			this.pubnub.removeListener(options);
		};
		this.pubnub.addListener(options);
		return deregister;
	}

	/**
	 * Broadcast data on this channel
	 * @param {*} data  Any data you wish, typically an object
	 * @param {Object} options  options to send directly to the PubNub SDK
	 * @see https://www.pubnub.com/docs/web-javascript/api-reference-publish-and-subscribe#publish
	 * @returns {Promise}  Resolves when broadcast is complete
	 */
	publish(data, options = {}) {
		return new Promise((resolve, reject) => {
			_.defaults(options, {
				channel: this.pubnubName,
				message: data,
			});
			this.pubnub.publish(options, (status, response) => {
				if (status.error) {
					reject(status);
				} else {
					resolve(response);
				}
			});
		});
	}

	/**
	 * Get a report of people who are in this channel
	 * @param {Object} options  options to send directly to the PubNub SDK
	 * @returns {Promise<Object>}  Resolves to the report containing number of people
	 */
	hereNow(options = {}) {
		return new Promise((resolve, reject) => {
			_.defaults(options, {
				channels: [this.pubnubName],
			});
			this.pubnub.hereNow(options, (status, response) => {
				if (status.error) {
					reject(status);
				} else {
					resolve(response);
				}
			});
		});
	}

	/**
	 * Get the number of people who are in this channel
	 * @param {Object} options  options to send directly to the PubNub SDK
	 * @returns {Promise<Number>}  The number of people
	 */
	occupancy(options = {}) {
		_.defaults(options, {
			includeUUIDs: false,
		});
		return this.hereNow(options).then(report => report.totalOccupancy);
	}

	/**
	 * Unsubscribe from messages in this channel
	 * @returns {Promise}  Resolves when de-registration is complete
	 */
	destroy() {
		const ret = this.pubnub.unsubscribe({
			channels: [this.pubnubName],
		});
		return Promise.resolve(ret);
	}

	/**
	 * Register to messages of the given type; that is, when message is an object with the given type property
	 * @param {String} type  The name of the event
	 * @param {Function} handler  The handler to fire
	 * @param {Object} options  options to send directly to the PubNub SDK
	 * @returns {Function}  A function to de-register the listener
	 */
	on(type, handler, options = {}) {
		const filterer = (message, metadata) => {
			if (message.type == type) {
				handler(message, metadata);
			}
		};
		const deregister = () => {
			this.pubnub.removeListener(options);
		};
		this.subscribe(filterer, options);
		return deregister;
	}
}
